import { eachDayOfInterval, format, isEqual, parse, subDays } from "date-fns";
import { flatMap, groupBy, map, mapValues, orderBy } from "lodash";
import { dateFormat } from "../Shared";
export function isDeposit(t) {
    const type = t.transactionType.type;
    return (type === "Cash Deposit" || type === "Transfer In" || type === "Journal");
}
export function isWithdrawal(t) {
    const type = t.transactionType.type;
    return type === "Transfer Out" || type === "Cash Withdrawal";
}
export function isDepositOrWithdrawal(t) {
    return isDeposit(t) || isWithdrawal(t);
}
export function getDepositDataByDate(apiResponse) {
    const allTransactions = map(apiResponse, (val) => val.transactions).flat();
    const contributionTransactions = allTransactions.filter(isDepositOrWithdrawal);
    const contributionGroupedByDate = groupBy(contributionTransactions, (t) => format(t.settleDate, dateFormat));
    // use integer cents to have more accurate math
    const contributionSummed = mapValues(contributionGroupedByDate, (ts) => ts.reduce((prev, t) => prev + Math.round(t.marketValue * (isWithdrawal(t) ? -100 : 100)), 0) / 100);
    const sortedSummedContribution = orderBy(map(contributionSummed, (x, date) => [date, x]), [(tup) => tup[0]], ["asc"]);
    const cumulativeSummed = sortedSummedContribution.reduce((prev, d) => {
        const lastValue = prev.slice(-1)[0]?.[1] ?? 0;
        const cumulativeSum = Math.round(d[1] * 100 + lastValue * 100) / 100;
        return [...prev, [d[0], cumulativeSum]];
    }, []);
    return cumulativeSummed.map((t) => ({ date: t[0], value: t[1] }));
}
function isNotNull(x) {
    return x !== null;
}
/**
 *
 * @param apiResponse list of events
 * @param endDate `YYYY-MM-DD` date, inclusive.
 * @param startDate `YYYY-MM-DD` date, inclusive
 * @param startingValue value in dollars
 * @returns - returns a value for every day between `startDate` to `endDate` with values calculated from `startDate` to the current date
 */
export function generateCumulativeDailyNetDeposits(apiResponse, startDate, endDate) {
    const startAsDate = parse(startDate, dateFormat, new Date());
    const endAsDate = parse(endDate, dateFormat, new Date());
    const allTransactions = flatMap(apiResponse, (val) => val.transactions.map((x) => ({ ...x, accountId: val.accountId })));
    const depositTransactions = allTransactions.filter((isDeposit));
    const withdrawalTransactions = allTransactions.filter((isWithdrawal));
    const interval = eachDayOfInterval({ start: startAsDate, end: endAsDate });
    const depositsGrouped = groupBy(depositTransactions, (d) => format(d.settleDate, dateFormat));
    const withdrawalsGrouped = groupBy(withdrawalTransactions, (d) => format(d.settleDate, dateFormat));
    const dayBeforeStart = format(subDays(startAsDate, 1), dateFormat);
    const datePoints = interval
        .map((d) => format(d, dateFormat))
        .reduce((daysBefore, thisDay) => {
        const dayBefore = daysBefore.slice(-1)[0];
        const thisDayAsDate = parse(thisDay, dateFormat, new Date());
        // group all statingMarketValues by account id
        const groupedStartingMarketValue = groupBy(apiResponse, (a) => a.accountId);
        // All starting contributions that were done "today"
        const startingValueTodayInCents = apiResponse
            .map((a) => a.startingMarketValue)
            .filter(isNotNull)
            .filter((marketValue) => {
            const marketDate = marketValue.date;
            return isEqual(marketDate, thisDayAsDate);
        })
            .map((marketValue) => Math.round(marketValue.marketValue * 100))
            .reduce((prev, curr) => prev + curr, 0);
        const dayBeforeContributions = (dayBefore?.contributions ?? 0) + startingValueTodayInCents;
        const isPartOfStartingMarketValue = (x) => {
            const potentialAccount = groupedStartingMarketValue[x.accountId]?.[0];
            if (potentialAccount === undefined) {
                // This should be an exception
                return true;
            }
            const startingMarketValueDate = potentialAccount.startingMarketValue?.date;
            if (startingMarketValueDate === undefined) {
                return true;
            }
            if (isEqual(startingMarketValueDate, thisDayAsDate)) {
                return false;
            }
            return true;
        };
        // assumptions
        // 1. startingMarketValue is recorded at the end of the day for the date given and includes all deposits, withdrawals, transfer in and transfer out
        //
        // Thus
        // We want to filter all transactions out that are the same day as the account's startingMarketValue.date
        //
        // Steps to do this
        // 1. group deposits & withdrawals by account id and date
        // 2. if `thisDay` === apiResponse[number].startingMarketValue.date then use 0 for that account. This is b/c it's already part of the startingMarketValue.marketValue
        // use integer cents to have more accurate math
        const todaysContributions = (depositsGrouped[thisDay] ?? [])
            .filter(isPartOfStartingMarketValue)
            .reduce(reduceCentAdder, 0);
        const dayBeforeWithdrawals = dayBefore?.withdrawals ?? 0;
        // use integer cents to have more accurate math
        const todaysWithdrawals = (withdrawalsGrouped[thisDay] ?? [])
            .filter(isPartOfStartingMarketValue)
            .reduce(reduceCentAdder, 0);
        return [
            ...daysBefore,
            {
                date: thisDay,
                contributions: dayBeforeContributions + todaysContributions,
                withdrawals: dayBeforeWithdrawals + todaysWithdrawals,
            },
        ];
    }, [
        { date: dayBeforeStart, contributions: 0, withdrawals: 0 },
    ])
        .slice(1) // chop off the starting entry of days before the asked for start date
        .map((d) => ({
        ...d,
        contributions: d.contributions / 100, // convert from integer cents to fractional dollars,
        withdrawals: d.withdrawals / 100, // convert from integer cents to fractional dollars,
    }));
    return datePoints;
}
const reduceCentAdder = (prev, t) => prev + Math.round(t.marketValue * 100);
//# sourceMappingURL=getEvents.js.map